import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type UserListQuery } from '@utazzo/shared';
import { UzBadge, UzButton, UzDialog, UzTextarea } from '@utazzo/ui';
import { Check, ExternalLink, Info, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  CATALOG_STATUS_LABEL,
  TYPE_LABEL,
  catalogDetailPath,
  formatWhen,
  statusTone,
} from '../catalog-approval-format';
import {
  bulkApproveCatalogItems,
  bulkRejectCatalogItems,
  listCatalogApprovals,
  type BulkDecisionResult,
  type CatalogApprovalRef,
  type CatalogApprovalsQuery,
  type CatalogType,
  type PendingApprovalItem,
} from '../catalog-approvals.api';
import {
  CatalogApprovalReviewDialog,
  REJECTION_REASON_MAX,
  rejectionReasonError,
  type CatalogApprovalReviewAction,
} from '../components/CatalogApprovalReviewDialog';
import { useCatalogApprovalsPendingCount } from '../use-catalog-approvals';

import { DataGrid, type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { PageShell } from '@/components/page-shell';
import { useUsersList } from '@/features/users/use-users';
import { useDataGridState } from '@/hooks/use-data-grid-state';
import { humanEnum } from '@/lib/enum-labels';

/** '' is the "All types" option - mirrors the Type filter on lead approvals. */
const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'ACTIVITY', label: 'Activities' },
  { value: 'TRANSFER', label: 'Transfers' },
  { value: 'HOTEL', label: 'Hotels' },
  { value: 'CRUISE', label: 'Cruises' },
  { value: 'MEAL_SERVICE', label: 'Meals' },
  { value: 'VISA', label: 'Visas' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'FIXED_DEPARTURE', label: 'Fixed departures' },
] as const;

const TYPE_FILTER: DataGridFilter = {
  id: 'type',
  label: 'Type',
  options: TYPE_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
  widthClass: 'w-40',
};

/**
 * Decision slice, mirroring the Status filter on the lead-approvals inbox.
 * PENDING reads the catalog tables; APPROVED / REJECTED read each product's
 * last decision out of the approval log.
 */
const QUEUE_STATUS_OPTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
] as const;

const QUEUE_STATUS_FILTER: DataGridFilter = {
  id: 'queueStatus',
  label: 'Status',
  options: QUEUE_STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
  widthClass: 'w-36',
};

/** Stable reference - it is the nuqs parser default. */
const PENDING_DEFAULT_FILTERS: Readonly<Record<string, string>> = { queueStatus: 'PENDING' };

/** Stable arrays - `useDataGridState` treats `filterIds` as a memo dependency,
 *  so a fresh literal per render would re-create every nuqs parser. */
const PENDING_FILTER_IDS = ['type', 'queueStatus', 'requestedBy', 'reviewedBy'] as const;
const STATIC_FILTERS: ReadonlyArray<DataGridFilter> = [TYPE_FILTER, QUEUE_STATUS_FILTER];

const parseQueueStatus = (
  value: string | undefined,
): 'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED' =>
  value === 'APPROVED' || value === 'REJECTED' || value === 'ALL' ? value : 'PENDING';

/** Frozen so it stays referentially stable - it is a react-query key. One page
 *  of 200 active users covers every realistic team without paging. */
const ACTOR_FILTER_USERS_QUERY: UserListQuery = {
  page: 1,
  pageSize: 200,
  sortDir: 'asc',
  sortBy: 'fullName',
  isActive: true,
};

const isCatalogType = (value: string | undefined): value is CatalogType =>
  value !== undefined && value in TYPE_LABEL;

const parseTypeFilter = (value: string | undefined): CatalogType | 'ALL' =>
  isCatalogType(value) ? value : 'ALL';

/* -------------------------------------------------------------------------- */
/* Queue                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The approval queue: everything across Activities / Transfers / Hotels / Meals
 * / Visas / Insurance / Fixed departures still awaiting an admin decision.
 * Approve or reject, singly or in bulk.
 */
const ApprovalsQueue = (): React.JSX.Element => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  // Read-only detail modal - opens on row-click and via the per-row Info
  // button; `action` non-null (the Approve / Reject buttons) opens straight on
  // the confirmation step. Mirrors the lead-approvals inbox.
  const [dialogTarget, setDialogTarget] = useState<{
    row: PendingApprovalItem;
    action: CatalogApprovalReviewAction | null;
  } | null>(null);
  // Bulk Reject confirms first, the same way a single rejection does - the
  // reason is mandatory, and one reason is recorded against every item in the
  // selection. Holds the already-filtered refs so the dialog only reports what
  // it will actually send.
  const [bulkRejectTarget, setBulkRejectTarget] = useState<CatalogApprovalRef[] | null>(null);
  const [bulkReason, setBulkReason] = useState('');
  const [bulkReasonTouched, setBulkReasonTouched] = useState(false);
  const gridState = useDataGridState({
    filterIds: PENDING_FILTER_IDS,
    defaultFilters: PENDING_DEFAULT_FILTERS,
  });
  const { state } = gridState;

  const queueStatus = parseQueueStatus(state.filters.queueStatus as string | undefined);
  // The All view mixes waiting and decided rows, so "can I decide here" is a
  // per-row question (`decidedAt`), not a per-view one.
  const canDecide = queueStatus === 'PENDING' || queueStatus === 'ALL';

  const query: CatalogApprovalsQuery = {
    type: parseTypeFilter(state.filters.type),
    status: queueStatus,
    // '' (the "All members" / "Anyone" option) -> undefined = no filter.
    requestedById: (state.filters.requestedBy as string | undefined) || undefined,
    reviewedById: (state.filters.reviewedBy as string | undefined) || undefined,
    search: state.q || undefined,
    page: state.page,
    pageSize: state.pageSize,
  };
  const listQuery = useQuery({
    queryKey: ['catalog-approvals', query],
    queryFn: () => listCatalogApprovals(query),
  });

  // Populates the two people filters. One 200-row page covers every realistic
  // team, and the same roster serves both dropdowns.
  const usersQuery = useUsersList(ACTOR_FILTER_USERS_QUERY);
  const filters = useMemo((): ReadonlyArray<DataGridFilter> => {
    const people = (usersQuery.data?.data ?? []).map((u) => ({ value: u.id, label: u.fullName }));
    return [
      ...STATIC_FILTERS,
      {
        id: 'requestedBy',
        label: 'Requested by',
        options: [{ value: '', label: 'All members' }, ...people],
        widthClass: 'w-48',
      },
      {
        id: 'reviewedBy',
        label: 'Approved by',
        // Pending rows have no reviewer, so picking anyone here narrows to
        // rows that have actually been actioned - same as lead approvals.
        options: [{ value: '', label: 'Anyone' }, ...people],
        widthClass: 'w-48',
      },
    ];
  }, [usersQuery.data]);

  // Broad key: a decision moves the row between the Pending / Approved /
  // Rejected slices, and each slice is its own cache entry.
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['catalog-approvals'] });
  };
  // Bulk decisions return partial success, so the toast reports both halves -
  // "12 approved" alone would hide the 3 that didn't take.
  const reportBulk = (verb: 'approved' | 'rejected') => (res: BulkDecisionResult) => {
    invalidate();
    if (res.updated > 0) toast.success(`${res.updated} ${verb}`);
    if (res.failed.length > 0) toast.error(`${res.failed.length} could not be ${verb}`);
  };
  const bulkApprove = useMutation({
    mutationFn: (items: CatalogApprovalRef[]) => bulkApproveCatalogItems(items),
    onSuccess: reportBulk('approved'),
    onError: () => toast.error('Failed to approve the selected products'),
  });
  const closeBulkReject = (): void => {
    setBulkRejectTarget(null);
    setBulkReason('');
    setBulkReasonTouched(false);
  };
  const bulkReject = useMutation({
    mutationFn: ({ items, note }: { items: CatalogApprovalRef[]; note: string }) =>
      bulkRejectCatalogItems(items, note),
    onSuccess: (res: BulkDecisionResult) => {
      reportBulk('rejected')(res);
      closeBulkReject();
    },
    onError: () => toast.error('Failed to reject the selected products'),
  });
  const bulkReasonError = rejectionReasonError(bulkReason);
  const confirmBulkReject = (): void => {
    if (!bulkRejectTarget) return;
    setBulkReasonTouched(true);
    if (bulkReasonError) return;
    bulkReject.mutate({ items: bulkRejectTarget, note: bulkReason.trim() });
  };

  const rows = listQuery.data?.data ?? [];
  const pagination = listQuery.data
    ? {
        page: listQuery.data.pagination.page,
        pageSize: listQuery.data.pagination.pageSize,
        totalItems: listQuery.data.pagination.totalItems,
      }
    : null;

  const columns = useMemo((): ReadonlyArray<DataGridColumn<PendingApprovalItem>> => {
    const cols: DataGridColumn<PendingApprovalItem>[] = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Product',
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{TYPE_LABEL[row.entityType]}</span>
          </div>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        width: '120px',
        cell: (row) => <span className="text-sm text-zinc-700">{TYPE_LABEL[row.entityType]}</span>,
      },
      {
        id: 'location',
        header: 'Location',
        cell: (row) => (
          <div className="text-sm text-zinc-700">
            {row.city ?? '-'}
            {row.country ? (
              <span className="block text-xs text-zinc-500">{row.country}</span>
            ) : null}
          </div>
        ),
      },
      {
        id: 'change',
        header: 'Requested change',
        width: '150px',
        cell: (row) =>
          row.hasPendingChange ? (
            <UzBadge tone="warning" pill>
              Edit pending
            </UzBadge>
          ) : (
            <span className="text-sm text-zinc-600">New product</span>
          ),
      },
      {
        id: 'requestedBy',
        header: 'Requested by',
        width: '150px',
        cell: (row) => <span className="text-sm text-zinc-700">{row.submittedByName ?? '-'}</span>,
      },
      {
        id: 'submittedAt',
        header: 'Submitted',
        width: '140px',
        cell: (row) => <span className="text-sm text-zinc-600">{formatWhen(row.submittedAt)}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        width: '110px',
        cell: (row) => (
          <UzBadge tone={statusTone(row.status)} pill>
            {humanEnum(CATALOG_STATUS_LABEL, row.status)}
          </UzBadge>
        ),
      },
      {
        id: 'reviewer',
        header: 'Reviewer',
        width: '160px',
        // The catalog queue routes to nobody in particular - unlike lead
        // approvals, where a request is assigned to one team lead - so an
        // undecided row belongs to the admin pool rather than reading as a
        // missing value. Once decided, it names who decided and when.
        cell: (row) =>
          row.decidedByName ? (
            <div className="text-sm text-zinc-700">
              {row.decidedByName}
              <span className="block text-xs text-zinc-500">
                {formatWhen(row.decidedAt ?? null)}
              </span>
            </div>
          ) : row.decidedAt ? (
            <span className="text-sm text-zinc-400">-</span>
          ) : (
            <span className="text-sm text-zinc-500">Admin pool</span>
          ),
      },
    ];

    // Details / Open product stay available on decided rows too - only the
    // decision buttons below are gated.
    cols.push({
      id: 'actions',
      header: '',
      width: canDecide ? '280px' : '120px',
      align: 'right',
      truncate: false,
      resizable: false,
      alwaysVisible: true,
      cell: (row) => (
        <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          <UzButton
            size="sm"
            variant="ghost"
            onClick={() => setDialogTarget({ row, action: null })}
            title="View request details"
          >
            <Info className="h-4 w-4" />
          </UzButton>
          <UzButton
            size="sm"
            variant="ghost"
            onClick={() => navigate(catalogDetailPath(row.entityType, row.id))}
            title="Open product"
          >
            <ExternalLink className="h-4 w-4" />
          </UzButton>
          {/* In the All view the same column carries already-decided rows,
              which have nothing left to action. */}
          {canDecide && !row.decidedAt && (
            <>
              <UzButton size="sm" onClick={() => setDialogTarget({ row, action: 'approve' })}>
                <Check className="mr-1 h-4 w-4" />
                Approve
              </UzButton>
              <UzButton
                size="sm"
                variant="outline"
                onClick={() => setDialogTarget({ row, action: 'reject' })}
              >
                <X className="mr-1 h-4 w-4" />
                Reject
              </UzButton>
            </>
          )}
        </div>
      ),
    });

    return cols;
  }, [canDecide, navigate, state.page, state.pageSize]);

  return (
    <>
      <DataGrid
        gridId="catalog-approvals"
        data={rows}
        columns={columns}
        filters={filters}
        getRowId={(row) => `${row.entityType}-${row.id}`}
        isLoading={listQuery.isLoading}
        isError={listQuery.isError}
        onRetry={() => void listQuery.refetch()}
        pagination={pagination}
        state={state}
        onStateChange={gridState}
        onRowClick={(row) => setDialogTarget({ row, action: null })}
        searchPlaceholder="Search name / city / country"
        emptyTitle={
          queueStatus === 'PENDING'
            ? 'Nothing pending approval'
            : queueStatus === 'ALL'
              ? 'No approval activity yet'
              : `No ${queueStatus.toLowerCase()} products yet`
        }
        emptyDescription={
          queueStatus === 'PENDING'
            ? 'Products appear here when a team member submits a new activity, transfer, hotel, meal or visa - or an edit to an approved one. Switch Status to see what has already been decided.'
            : 'Decisions are read from the approval log, so this fills up as products are approved or rejected.'
        }
        tableLayout="auto"
        selectable={canDecide}
        // `getRowId` is composite (`TYPE-id`) because the queue spans seven
        // catalog tables, so the bulk actions read `rows` - not `ids` - to
        // recover each item's type for the API. In the All view a selection can
        // include already-decided rows; those are dropped rather than sent and
        // bounced back as per-row failures.
        bulkActions={
          !canDecide
            ? []
            : [
                {
                  id: 'bulk-approve',
                  label: 'Approve',
                  icon: <Check className="h-4 w-4" />,
                  onAction: (_ids, selected) => {
                    void bulkApprove.mutateAsync(
                      selected
                        .filter((r) => !r.decidedAt)
                        .map((r) => ({ type: r.entityType, id: r.id })),
                    );
                  },
                },
                {
                  id: 'bulk-reject',
                  label: 'Reject',
                  danger: true,
                  icon: <X className="h-4 w-4" />,
                  onAction: (_ids, selected) => {
                    setBulkReason('');
                    setBulkReasonTouched(false);
                    setBulkRejectTarget(
                      selected
                        .filter((r) => !r.decidedAt)
                        .map((r) => ({ type: r.entityType, id: r.id })),
                    );
                  },
                },
              ]
        }
      />

      {/* Detail view. Opens on row-click OR via the per-row Info button, on
          pending and decided rows alike; `action` non-null (the per-row
          Approve / Reject) skips straight to the confirmation step. */}
      <CatalogApprovalReviewDialog
        item={dialogTarget?.row ?? null}
        initialAction={dialogTarget?.action ?? null}
        onClose={() => setDialogTarget(null)}
        footerLeft={
          dialogTarget ? (
            <UzButton
              variant="ghost"
              onClick={() =>
                navigate(catalogDetailPath(dialogTarget.row.entityType, dialogTarget.row.id))
              }
            >
              <ExternalLink className="mr-1 h-4 w-4" />
              Open product
            </UzButton>
          ) : null
        }
      />

      {/* Bulk rejection confirmation - same contract as a single rejection:
          nothing is sent until a reason is given. */}
      <UzDialog
        open={bulkRejectTarget !== null}
        onOpenChange={(open) => {
          if (!open && !bulkReject.isPending) closeBulkReject();
        }}
        title={`Reject ${bulkRejectTarget?.length ?? 0} product${
          (bulkRejectTarget?.length ?? 0) === 1 ? '' : 's'
        }`}
        description="Each captured edit is discarded and the live version stays as it is. A product awaiting its first approval goes back to Draft. The reason below is recorded on every item's approval trail."
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <UzButton variant="ghost" onClick={closeBulkReject} disabled={bulkReject.isPending}>
              Cancel
            </UzButton>
            <UzButton
              variant="outline"
              onClick={confirmBulkReject}
              loading={bulkReject.isPending}
              disabled={bulkRejectTarget?.length === 0}
            >
              Confirm rejection
            </UzButton>
          </div>
        }
      >
        <div className="space-y-3 text-sm text-zinc-700">
          {bulkRejectTarget?.length === 0 ? (
            <p>Nothing in this selection is still awaiting a decision.</p>
          ) : (
            <p>
              <strong>{bulkRejectTarget?.length ?? 0}</strong> pending product
              {(bulkRejectTarget?.length ?? 0) === 1 ? '' : 's'} will be rejected.
            </p>
          )}
          <UzTextarea
            name="bulkRejectReason"
            label="Rejection reason"
            placeholder="What needs to change before these can be approved?"
            required
            hint="Applied to every selected product and shown to each submitter."
            error={bulkReasonTouched && bulkReasonError ? bulkReasonError : undefined}
            maxLength={REJECTION_REASON_MAX}
            showCount
            rows={3}
            value={bulkReason}
            onChange={(e) => {
              setBulkReason(e.target.value);
              if (bulkReasonTouched) setBulkReasonTouched(false);
            }}
            disabled={bulkReject.isPending}
          />
        </div>
      </UzDialog>
    </>
  );
};

/* -------------------------------------------------------------------------- */
/* Page                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Product approvals - the single decision queue for Activities / Transfers /
 * Hotels / Meals / Visas / Insurance / Fixed departures.
 *
 * One grid, no tabs: which slice you are looking at is a filter, exactly like
 * the lead-approvals inbox. Status defaults to Pending; switch it to Approved
 * or Rejected to see what has already been decided, and narrow further with
 * Type / Requested by / Approved by.
 */
export const CatalogApprovalsPage = (): React.JSX.Element => {
  const pendingCount = useCatalogApprovalsPendingCount().data?.pagination.totalItems ?? 0;

  return (
    <PageShell
      title={pendingCount > 0 ? `Product approvals (${pendingCount} pending)` : 'Product approvals'}
      description="Activities, transfers, hotels, meals, visas, insurance and fixed departures awaiting your review. Use the Status filter to see what has already been approved or rejected."
    >
      <ApprovalsQueue />
    </PageShell>
  );
};
